/* eslint-disable react/no-array-index-key */

import React, { FC, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useDebounce } from "react-use";
import { Button, TextField } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";

import { SearchYellowIcon } from "@/assets/icons";
import { StyledIndications } from "@/components/features/Authentication/styled";
import { handleHighlight } from "@/core/html";
import { sanitizeHtml } from "@/core/security";
import { isNumeric } from "@/core/string";
import { brokerApi } from "@/shared/apis";
import { UserState } from "@/shared/domain";
import { useAuth, useNavigation } from "@/shared/hooks";

import { StyledDisplayBrokerContainer } from "../../styled";
import {
    StyledAutoCompleteField,
    StyledBrokerAddress,
    StyledBrokerName,
    StyledSearchMinLettersRequired,
} from "./styled";

type RegisterChooseBrokerSelectProps = {
    onSelectBroker: (officeId: string) => void;
    filterUserBrokers?: boolean;
};

const RegisterChooseBrokerSelect: FC<RegisterChooseBrokerSelectProps> = ({
    onSelectBroker,
    filterUserBrokers = false,
}) => {
    const { navToRoot } = useNavigation();
    const { userState } = useAuth();

    const { t } = useTranslation();

    const [searchWord, setSearchWord] = useState("");
    const [debouncedSearchWord, setDebouncedSearchWord] = useState("");

    const {
        data: { result: availableBrokers },
    } = brokerApi.useQueryBrokers({
        enabled: filterUserBrokers,
    });

    const { data: brokers = [], isFetching } = useQuery(
        ["brokers", { query: debouncedSearchWord }],
        () => brokerApi.searchBrokers(debouncedSearchWord),
        {
            enabled: debouncedSearchWord !== "",
            suspense: false,
        },
    );

    const handleOnSelectBroker = (selectedBroker: MyBrokerApi.Broker) => {
        if (selectedBroker && isNumeric(selectedBroker.officeId)) {
            onSelectBroker(selectedBroker.officeId);
        }
    };

    useDebounce(
        () => {
            if (searchWord.length > 2) {
                setDebouncedSearchWord(searchWord);
            } else {
                setDebouncedSearchWord("");
            }
        },
        400,
        [searchWord],
    );

    useEffect(() => {
        handleHighlight(debouncedSearchWord, true);
    }, [brokers]);

    let noResultsMessage = "";
    if (debouncedSearchWord.length < 3) noResultsMessage = t("Common.StartSearchMinimunLetters", { number: 3 });
    if (debouncedSearchWord.length >= 3 && brokers.length === 0) noResultsMessage = t("Common.NoResults");

    return (
        <div className="container" data-testid="RegisterChooseBrokerSelect" style={{ textAlign: "center" }}>
            <StyledDisplayBrokerContainer>
                <StyledIndications>
                    <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(t("Registration.ChooseBroker.Message")) }} />
                </StyledIndications>
                <br />

                <StyledAutoCompleteField
                    autoHighlight
                    classes={{
                        root: "autocomplete-root",
                        input: "autocomplete-input",
                        paper: "autocomplete-paper",
                        endAdornment: "autocomplete-endAdornment",
                    }}
                    id="brokerAutocomplete"
                    noOptionsText={noResultsMessage}
                    disableClearable
                    getOptionDisabled={(option: MyBrokerApi.Broker) =>
                        availableBrokers?.map?.((broker) => broker.officeId).includes(option.officeId)
                    }
                    onChange={(_e, newValue) => handleOnSelectBroker(newValue)}
                    onOpen={() => setTimeout(() => handleHighlight(debouncedSearchWord), 0)}
                    loading={isFetching}
                    options={brokers}
                    getOptionLabel={(option: MyBrokerApi.Broker) =>
                        `${option.brokerName}  ${option.addressStringFrontEnd} ${option.officeId}`
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={t("Registration.ChooseBroker.Placeholder")}
                            onChange={(e) => setSearchWord(e.target.value)}
                            InputProps={{
                                ...params.InputProps,
                                inputProps: {
                                    ...params.inputProps,
                                    name: "broker",
                                    "data-testid": "autocomplete_input-broker",
                                    id: "autocomplete_input-broker",
                                },
                                startAdornment: (
                                    <InputAdornment position="start" style={{ marginLeft: "8px", marginRight: "0" }}>
                                        <SearchYellowIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option: MyBrokerApi.Broker) => {
                        return (
                            <li
                                {...props}
                                data-testid={`search-broker-option-${option.officeId}`}
                                style={{ flexDirection: "column", alignItems: "start" }}
                            >
                                <StyledBrokerName className="highlightText">{option.brokerName}</StyledBrokerName>
                                <StyledBrokerAddress className=" highlightText">
                                    {option.addressStringFrontEnd}
                                </StyledBrokerAddress>
                            </li>
                        );
                    }}
                />
                <StyledSearchMinLettersRequired>{noResultsMessage}</StyledSearchMinLettersRequired>
            </StyledDisplayBrokerContainer>
            {userState === UserState.UNKNOWN && (
                <div className="form-bottom-container">
                    <Button fullWidth onClick={() => navToRoot()}>
                        {t("Registration.ChooseBroker.AlreadyHaveAccount")}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default RegisterChooseBrokerSelect;
